// components/models/GeminiOmniFlashControlPanel.tsx
import React, { useCallback, useRef } from 'react';
import AutoGrowingTextarea from '../AutoGrowingTextarea';
import RangeControl from '../ui/RangeControl';
import Select from '../ui/Select';
import Switch from '../ui/Switch';
import VideoInputRangeControls from './VideoInputRangeControls';
import { GeminiOmniFlashAspectRatio, GeminiOmniFlashParams, GeminiOmniFlashReferenceInputMode, LockableFile } from '#types/types';
import { useKernel } from '@/kernel/Kernel';
import { Upload, X } from '../ui/lucide-memo';

interface GeminiOmniFlashControlPanelProps {
  params: GeminiOmniFlashParams;
  setModelParams: (
    params: (prevParams: { [model: string]: GeminiOmniFlashParams }) => { [model: string]: GeminiOmniFlashParams }
  ) => void;
  diffusionModel: string;
  audioReferences?: LockableFile[];
  onAudioReferencesChange?: (files: LockableFile[]) => void;
}

const defaultGeminiOmniFlashParams: GeminiOmniFlashParams = {
  prompt: '',
  duration: 5,
  reference_input_mode: 'frame',
  video_input_start_ms: null,
  video_input_end_ms: null,
  aspect_ratio: 'auto',
  use_audio_input: false,
  seed: -1,
};

export const getDefaultGeminiOmniFlashParams = (): GeminiOmniFlashParams => ({ ...defaultGeminiOmniFlashParams });


const REFERENCE_INPUT_MODES: GeminiOmniFlashReferenceInputMode[] = ['frame', 'video'];
const ASPECT_RATIO_OPTIONS: GeminiOmniFlashAspectRatio[] = ['auto', '16:9', '9:16'];

const GeminiOmniFlashControlPanel: React.FC<GeminiOmniFlashControlPanelProps> = React.memo(({
  params,
  setModelParams,
  diffusionModel,
  audioReferences = [],
  onAudioReferencesChange,
}) => {
  const { kernel } = useKernel();
  const audioInputRef = useRef<HTMLInputElement>(null);
  const referenceInputMode: GeminiOmniFlashReferenceInputMode =
    params.reference_input_mode === 'video' ? 'video' : 'frame';
  const aspectRatio: GeminiOmniFlashAspectRatio =
    ASPECT_RATIO_OPTIONS.includes(params.aspect_ratio as GeminiOmniFlashAspectRatio)
      ? params.aspect_ratio as GeminiOmniFlashAspectRatio
      : 'auto';
  const useAudioInput = params.use_audio_input === true;

  const updateModelParams = useCallback((patch: Partial<GeminiOmniFlashParams>) => {
    setModelParams(prev => {
      const current = prev[diffusionModel];
      if (!current) {
        console.error(`[GeminiOmniFlashControlPanel] Missing params for model "${diffusionModel}".`);
        return prev;
      }
      return {
        ...prev,
        [diffusionModel]: {
          ...current,
          ...patch,
        },
      };
    });
  }, [diffusionModel, setModelParams]);

  const handleParamChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const parsedValue = type === 'number' || type === 'range' ? parseFloat(value) : value;
    updateModelParams({ [name]: parsedValue } as Partial<GeminiOmniFlashParams>);

    kernel.current?.logUserAction('Setting Changed', {
      setting_name: name,
      ...(name === 'prompt' ? { value_length: String(parsedValue).length } : { value: parsedValue }),
      model_name: diffusionModel,
      source_component: 'GeminiOmniFlashControlPanel',
    });
  }, [diffusionModel, kernel, updateModelParams]);

  const handleReferenceInputModeChange = useCallback((value: string) => {
    const nextMode: GeminiOmniFlashReferenceInputMode = value === 'video' ? 'video' : 'frame';
    updateModelParams({ reference_input_mode: nextMode });
    kernel.current?.logUserAction('Setting Changed', {
      setting_name: 'reference_input_mode',
      value: nextMode,
      model_name: diffusionModel,
      source_component: 'GeminiOmniFlashControlPanel',
    });
  }, [diffusionModel, kernel, updateModelParams]);

  const handleAspectRatioChange = useCallback((value: string) => {
    const nextAspectRatio: GeminiOmniFlashAspectRatio =
      value === '16:9' || value === '9:16' ? value : 'auto';
    updateModelParams({ aspect_ratio: nextAspectRatio });
    kernel.current?.logUserAction('Setting Changed', {
      setting_name: 'aspect_ratio',
      value: nextAspectRatio,
      model_name: diffusionModel,
      source_component: 'GeminiOmniFlashControlPanel',
    });
  }, [diffusionModel, kernel, updateModelParams]);

  const handleAudioInputToggle = useCallback((checked: boolean) => {
    updateModelParams({ use_audio_input: checked });
    kernel.current?.logUserAction('Setting Changed', {
      setting_name: 'use_audio_input',
      value: checked,
      model_name: diffusionModel,
      source_component: 'GeminiOmniFlashControlPanel',
    });
  }, [diffusionModel, kernel, updateModelParams]);

  const handleVideoInputRangeChange = useCallback((
    name: 'video_input_start_ms' | 'video_input_end_ms',
    value: number | null,
  ) => {
    updateModelParams({ [name]: value } as Partial<GeminiOmniFlashParams>);
    kernel.current?.logUserAction('Setting Changed', {
      setting_name: name,
      value,
      model_name: diffusionModel,
      source_component: 'GeminiOmniFlashControlPanel',
    });
  }, [diffusionModel, kernel, updateModelParams]);

  const handleAudioInputChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || [])
      .filter((file) => file.type.startsWith('audio/'));
    event.target.value = '';
    if (!selectedFiles.length || !onAudioReferencesChange) return;
    const { v4: uuidv4 } = await import('uuid');
    onAudioReferencesChange(selectedFiles.slice(0, 1).map((file) => ({
      id: uuidv4(),
      file,
      locked: false,
    })));
  }, [onAudioReferencesChange]);

  const handleOpenAudioPicker = useCallback(() => {
    audioInputRef.current?.click();
  }, []);

  const handleRemoveAudioReference = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    onAudioReferencesChange?.([]);
  }, [onAudioReferencesChange]);

  return (
    <>
      <div className="mb-4">
        <AutoGrowingTextarea
          label="Prompt"
          placeholder="Describe the video you want to generate or edit..."
          name="prompt"
          value={params.prompt}
          onChange={handleParamChange}
          onEnterPress={kernel.current?.triggerGenerate}
          maxHeight={600}
          labelClassName="block text-lg font-semibold text-white mb-2"
        />
      </div>

      <div className="mb-4">
        <RangeControl
          label="Duration"
          name="duration"
          min={3}
          max={10}
          step={1}
          value={params.duration}
          onChange={handleParamChange}
          description="Seconds. Gemini Omni Flash currently supports short 720p clips."
        />
      </div>

      <div className="mb-4">
        <Select
          id="gemini-omni-reference-input-mode"
          label="Input Mode"
          value={referenceInputMode}
          options={REFERENCE_INPUT_MODES}
          onChange={handleReferenceInputModeChange}
        />
      </div>

      <div className="mb-4">
        <Select
          id="gemini-omni-aspect-ratio"
          label="Aspect Ratio"
          value={aspectRatio}
          options={ASPECT_RATIO_OPTIONS}
          onChange={handleAspectRatioChange}
        />
      </div>

      {referenceInputMode === 'video' && (
        <div className="mb-4">
          <VideoInputRangeControls
            startMs={params.video_input_start_ms}
            endMs={params.video_input_end_ms}
            onChange={handleVideoInputRangeChange}
          />
        </div>
      )}

      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-medium text-white">Audio Input</div>
          <div className="text-xs text-white/40">Attach one audio file as an additional Gemini input.</div>
        </div>
        <Switch
          checked={useAudioInput}
          onCheckedChange={handleAudioInputToggle}
          labelRight="On"
        />
      </div>

      {useAudioInput && (
        <div className="mb-4">
          <input
            ref={audioInputRef}
            type="file"
            accept="audio/*"
            className="hidden"
            onChange={handleAudioInputChange}
          />
          <button
            type="button"
            onClick={handleOpenAudioPicker}
            className="flex min-h-12 w-full items-center justify-center gap-2 rounded-lg border border-dashed border-white/10 bg-white/[0.04] px-3 py-2 text-sm text-white/70 hover:bg-white/[0.07]"
          >
            <Upload size={16} />
            {audioReferences.length > 0 ? 'Replace audio input' : 'Attach audio input'}
          </button>
          {audioReferences.length > 0 && (
            <div className="mt-2 flex items-center justify-between gap-2 rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-xs text-white/70">
              <span className="min-w-0 truncate">{audioReferences[0]?.file.name}</span>
              <button
                type="button"
                onClick={handleRemoveAudioReference}
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-white/50 hover:bg-white/10 hover:text-white"
                aria-label="Remove audio input"
              >
                <X size={14} />
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
});

GeminiOmniFlashControlPanel.displayName = 'GeminiOmniFlashControlPanel';

export default GeminiOmniFlashControlPanel;
